/** News types. These mirror the Pydantic schemas in `api/api/schemas/news.py`. */

/**
 * The content taxonomy has two closed layers, mirroring `api/api/schemas/content.py`.
 *
 * `ContentFormat` is what a piece of content *is* — one value, and the one that
 * decides what the client renders. `ContentTopic` is what it is *about* — one to
 * three values. Slugs stay Czech on the wire: they are identity, not copy, and are
 * labelled through the `formats.*` and `topics.*` catalogues.
 */
export type ContentFormat = "clanek" | "video" | "protokol" | "zaznam-webinare";

export const CONTENT_FORMATS: readonly ContentFormat[] = [
  "clanek",
  "video",
  "protokol",
  "zaznam-webinare",
];

export type ContentTopic =
  | "energie"
  | "soustredeni"
  | "regenerace-spanek"
  | "metabolismus"
  | "imunita"
  | "vykon-sport"
  | "suplementace"
  | "novinky-z-elyxeeru";

export const CONTENT_TOPICS: readonly ContentTopic[] = [
  "energie",
  "soustredeni",
  "regenerace-spanek",
  "metabolismus",
  "imunita",
  "vykon-sport",
  "suplementace",
  "novinky-z-elyxeeru",
];

export interface NewsArticleSummary {
  id: string;
  slug: string;
  locale: string;
  title: string;
  excerpt: string | null;
  cover_image_url: string | null;
  /** One to three, from the closed vocabulary. Labelled through `topics.*`. */
  topics: ContentTopic[];
  format: ContentFormat;
  published_at: string;
}

export interface NewsArticleDetail extends NewsArticleSummary {
  /** Markdown. Rendered client-side so the API stays free of presentation concerns. */
  body: string;
  /**
   * Up to ten further 16:9 photographs, browsed in the cover's place, in the
   * editor's order. Detail-only: the feed card shows the cover alone.
   */
  gallery_image_urls: string[];
  /** Search/editorial metadata; not rendered as article copy. */
  keywords: string[];
  /** Plain scientific citations, rendered after the Markdown body. */
  references: string[];
  /** A bare YouTube id, never a URL. Null unless the article carries a video. */
  video_youtube_id: string | null;
  video_duration_seconds: number | null;
}

export interface NewsArticlePage {
  items: NewsArticleSummary[];
  total: number;
  limit: number;
  offset: number;
}

/** One selectable topic and how many visible articles carry it. */
export interface NewsTopicFacet {
  topic: ContentTopic;
  count: number;
}

/** Every topic, including the ones nothing carries yet — the chips must not move. */
export interface NewsTopicList {
  items: NewsTopicFacet[];
}

/** One selectable format and how many visible articles carry it. */
export interface NewsFormatFacet {
  format: ContentFormat;
  count: number;
}

export interface NewsFormatList {
  items: NewsFormatFacet[];
}
